import { escapeHtml } from '@/lib/html-escape'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/** Ô không có dữ liệu in dấu gạch dài thay vì để trắng, cho khớp cách hiển thị
 *  trên màn chi tiết phiếu. */
const DASH = '—'

export interface StockInNotePrintCompany {
    name: string
    taxCode?: string
    address?: string
    phone?: string
    /** Logo đã đọc sẵn thành data URL — ảnh tải qua mạng không kịp trước lệnh in. */
    logoDataUrl?: string
}

export interface StockInNotePrintRow {
    lotCode: string
    name: string
    quantity: number
    unit: string
    position: string
    /** M-196: số bao người in vừa nhập cho dòng tính theo kg. Không lưu ở đâu
     *  cả — chỉ in ra giấy. Rỗng = dòng không hỏi số bao. */
    bags?: number
}

export interface StockInNotePrintData {
    company?: StockInNotePrintCompany
    /** Tên kho hiển thị dưới tiêu đề, ví dụ "Kho hàng bán". */
    warehouseLabel: string
    noteCode: string
    /** Lý do nhập đã dịch sẵn — mỗi kho có bộ lý do riêng nên nơi gọi truyền vào. */
    reasonLabel: string
    statusLabel: string
    createdAt: string
    createdByName: string
    /** Rỗng khi phiếu chưa duyệt — ô ký vẫn in, chỉ khuyết tên. */
    approvedByName: string
    notes: string
    rows: StockInNotePrintRow[]
    /** M-196: in thêm cột "Số bao" (phiếu có dòng tính theo kg và người in đã
     *  nhập số bao). */
    showBags?: boolean
}

/** Dòng tính theo kg — đơn vị ghi tay nên có thể là "kg", "Kg", " KG ". */
export function isKgUnit(unit: string | null | undefined): boolean {
    return (unit ?? '').trim().toLowerCase() === 'kg'
}

/** Ngày giờ dạng `dd/MM/yyyy HH:mm`, khớp đúng định dạng phiếu đóng gói do
 *  server sinh (`02/01/2006 15:04`). Không dùng helper locale chung vì vi-VN
 *  mặc định đặt giờ trước ngày. */
function printDateTime(value: string): string {
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    const opts: Intl.DateTimeFormatOptions = {
        timeZone: 'Asia/Ho_Chi_Minh',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }
    const date = new Intl.DateTimeFormat('vi-VN', opts).format(d)
    const time = new Intl.DateTimeFormat('vi-VN', {
        timeZone: 'Asia/Ho_Chi_Minh',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).format(d)
    return `${date} ${time}`
}

function cell(value: string): string {
    const trimmed = value.trim()
    return trimmed ? escapeHtml(trimmed) : DASH
}

function signName(value: string): string {
    const trimmed = value.trim()
    return trimmed ? escapeHtml(trimmed) : ''
}

function companyBlock(company: StockInNotePrintCompany | undefined): string {
    if (!company) return ''
    // Chỉ nhận ảnh nhúng dạng data URL: giá trị này đi thẳng vào thuộc tính
    // `src` của iframe cùng nguồn, nên scheme lạ (`javascript:`) phải bị loại.
    const safeLogo = company.logoDataUrl?.startsWith('data:image/')
        ? escapeHtml(company.logoDataUrl)
        : ''
    const logo = safeLogo ? `<img class="logo" src="${safeLogo}" alt="" />` : ''
    // Thứ tự dòng bám theo `renderCompactHeader` của phiếu đóng gói
    // (server/internal/usecase/invoice/pdf_template.go): tên, địa chỉ, ĐT, MST.
    const address = company.address
        ? `<div>${escapeHtml(company.address)}</div>`
        : ''
    const phone = company.phone
        ? `<div>${m.sinp_company_phone()}: ${escapeHtml(company.phone)}</div>`
        : ''
    const taxCode = company.taxCode
        ? `<div>${m.sinp_company_tax_code()}: ${escapeHtml(company.taxCode)}</div>`
        : ''
    return `<div class="company">${logo}<div class="company-info"><div class="company-name">${escapeHtml(company.name)}</div>${address}${phone}${taxCode}</div></div>`
}

function bagsCell(bags: number | undefined): string {
    return bags === undefined ? DASH : formatQty(bags)
}

function rowsHtml(rows: StockInNotePrintRow[], showBags: boolean): string {
    return rows
        .map(
            (row, index) => `<tr class="row">
<td class="idx">${index + 1}</td>
<td class="code">${cell(row.lotCode)}</td>
<td>${cell(row.name)}</td>
<td class="qty">${formatQty(row.quantity)}</td>
<td class="unit">${cell(row.unit)}</td>
${showBags ? `<td class="bags">${bagsCell(row.bags)}</td>\n` : ''}<td class="pos">${cell(row.position)}</td>
</tr>`,
        )
        .join('')
}

/** Dựng HTML tự chứa của phiếu nhập kho khổ A4 dọc. Hàm thuần: không đụng DOM,
 *  không gọi mạng — nơi gọi tự nạp logo và bắn vào `printReceiptHtml`. */
export function buildStockInNotePrintHTML(data: StockInNotePrintData): string {
    const total = data.rows.reduce((sum, row) => sum + row.quantity, 0)
    const showBags = data.showBags === true
    const totalBags = data.rows.reduce((sum, row) => sum + (row.bags ?? 0), 0)
    return `<!DOCTYPE html>
<html lang="vi"><head><meta charset="utf-8" />
<title>${escapeHtml(data.noteCode)}</title>
<style>
@page { size: A4 portrait; margin: 12mm; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: 'Roboto', 'Segoe UI', system-ui, sans-serif; font-size: 12px; color: #000; background: #fff; line-height: 1.4; }
.company { display: flex; align-items: center; gap: 8mm; border-bottom: 1px solid #000; padding-bottom: 3mm; }
.logo { width: 20mm; height: auto; }
.company-name { font-size: 15px; font-weight: 700; text-transform: uppercase; }
.title { text-align: center; font-size: 20px; font-weight: 700; margin-top: 6mm; }
.subtitle { text-align: center; font-size: 13px; margin-bottom: 5mm; }
.meta { display: grid; grid-template-columns: 1fr 1fr; gap: 1mm 8mm; margin-bottom: 4mm; }
.meta .full { grid-column: 1 / -1; }
.meta .lbl { font-weight: 600; }
table { width: 100%; border-collapse: collapse; }
thead { display: table-header-group; }
tfoot { display: table-row-group; }
tr { break-inside: avoid; page-break-inside: avoid; }
th, td { border: 1px solid #000; padding: 4px 6px; vertical-align: top; }
th { font-weight: 700; text-align: center; }
.idx, .qty, .unit, .bags { text-align: center; }
.code { font-family: monospace; }
.total-lbl { text-align: right; font-weight: 700; }
.total-qty { text-align: center; font-weight: 700; }
.signs { margin-top: 6mm; break-inside: avoid; page-break-inside: avoid; }
.sign-date { text-align: right; font-style: italic; margin-bottom: 3mm; }
.sign-table { width: 100%; border-collapse: collapse; }
.sign-table td { border: 0; width: 50%; text-align: center; }
.sign-role { font-weight: 700; text-transform: uppercase; }
.sign-hint { font-style: italic; }
.sign-space { height: 22mm; }
.sign-name { font-weight: 600; }
</style></head>
<body>
${companyBlock(data.company)}
<div class="title">${m.sinp_title()}</div>
<div class="subtitle">${escapeHtml(data.warehouseLabel)}  |  ${m.sinp_note_code_label()}: ${escapeHtml(data.noteCode)}  |  ${m.sinp_date_label()}: ${escapeHtml(printDateTime(data.createdAt))}</div>
<div class="meta">
<div><span class="lbl">${m.sinp_label_reason()}:</span> ${cell(data.reasonLabel)}</div>
<div><span class="lbl">${m.sinp_label_status()}:</span> ${cell(data.statusLabel)}</div>
<div><span class="lbl">${m.sinp_label_created_by()}:</span> ${cell(data.createdByName)}</div>
<div><span class="lbl">${m.sinp_label_approved_by()}:</span> ${cell(data.approvedByName)}</div>
<div class="full"><span class="lbl">${m.sinp_label_notes()}:</span> ${cell(data.notes)}</div>
</div>
<table>
<thead><tr>
<th>${m.sinp_col_index()}</th>
<th>${m.sinp_col_lot_code()}</th>
<th>${m.sinp_col_name()}</th>
<th>${m.sinp_col_quantity()}</th>
<th>${m.sinp_col_unit()}</th>
${showBags ? `<th>${m.sinp_col_bags()}</th>\n` : ''}<th>${m.sinp_col_position()}</th>
</tr></thead>
<tbody>${rowsHtml(data.rows, showBags)}</tbody>
<tfoot><tr>
<td class="total-lbl" colspan="3">${m.sinp_total()}</td>
<td class="total-qty">${formatQty(total)}</td>
${
    showBags
        ? `<td></td>\n<td class="total-qty total-bags">${formatQty(totalBags)}</td>\n<td></td>`
        : '<td colspan="2"></td>'
}
</tr></tfoot>
</table>
<div class="signs">
<div class="sign-date">${m.sinp_date_line()}</div>
<table class="sign-table"><tr>
<td><div class="sign-role">${m.sinp_signer_creator()}</div><div class="sign-hint">${m.sinp_signer_hint()}</div><div class="sign-space"></div><div class="sign-name creator">${signName(data.createdByName)}</div></td>
<td><div class="sign-role">${m.sinp_signer_approver()}</div><div class="sign-hint">${m.sinp_signer_hint()}</div><div class="sign-space"></div><div class="sign-name approver">${signName(data.approvedByName)}</div></td>
</tr></table>
</div>
</body></html>`
}
